import { getLocalTimeZoneLabel } from 'js/lib/DateUtils';
import { cronToHumanReadable } from 'new/utils/cronDisplay';

/**
 * Calculates the next run time for a given cron expression and returns a user-friendly string.
 *
 * Complexity arises from:
 * 1. Parsing different cron patterns (fixed times, intervals with steps '/', ranges '-', lists ',').
 * 2. Cron expressions being in UTC, requiring calculation relative to current UTC time.
 * 3. Determining the *next* future occurrence, which involves:
 *    - Checking intervals (minutes, hours).
 *    - Handling specific days of week, days of month.
 *    - Handling step intervals for days (e.g., 1/2) or months (e.g., 1/2).
 *    - Correctly handling date rollovers (day -> month -> year).
 *    - Ensuring the calculated "next" time is strictly after the current time.
 * 4. Formatting the final UTC time into the user's local time with appropriate labels (previously Today/Tomorrow, now full date).
 * 5. Potential quirks with JavaScript Date object manipulation and timezone handling, especially in test environments.
 */
export const renderNextRunTime = (cronExpression: string): string => {
  if (!cronExpression) return 'Not scheduled';

  try {
    const currentTime = new Date();

    // Special case for "Once" schedule
    if (cronExpression.startsWith('once')) {
      //ideally we should never have this?
      return 'once';
    }

    //0 0/1 * 1/1 * ? * => Continuously
    if (cronExpression === '0 0/1 * 1/1 * ? *') {
      return 'Continuously';
    }

    //cron expression is in UTC. parse it first.

    // Parse the cron expression (UTC)
    const cronParts = cronExpression.split(' ');

    // Handle different cron part lengths
    if (cronParts.length < 5) {
      console.error('Invalid cron expression: too few parts');
      return 'Invalid schedule';
    }
    const localTimeZoneString = getLocalTimeZoneLabel();

    const minuteParts = cronParts[1]
      .split(',')
      .map((m) => parseInt(m, 10))
      .sort((a, b) => a - b);
    const hour = parseInt(cronParts[2], 10);
    const dayOfMonth = cronParts[3];
    const month = cronParts[4];

    // Handle optional parts
    const dayOfWeek = cronParts.length > 5 ? cronParts[5] : '?';
    const year = cronParts.length > 6 ? cronParts[6] : '*';

    // Check if this is a specific date/time (once) schedule
    if (
      dayOfMonth !== '*' &&
      dayOfMonth !== '?' &&
      month !== '*' &&
      month !== '?' &&
      year !== '*'
    ) {
      // Create a date in UTC
      const utcDate = new Date();
      utcDate.setUTCFullYear(parseInt(year, 10));
      utcDate.setUTCMonth(parseInt(month, 10) - 1); // Months are 0-based
      utcDate.setUTCDate(parseInt(dayOfMonth, 10));
      utcDate.setUTCHours(hour);
      utcDate.setUTCMinutes(minuteParts[0]);
      utcDate.setUTCSeconds(0);
      utcDate.setUTCMilliseconds(0);

      const nextRunTime = new Date(utcDate);
      const isPast = nextRunTime < currentTime;

      if (isPast) {
        return `Not scheduled`;
      }

      return `Once on ${nextRunTime.toLocaleString('en-US', {
        year: 'numeric',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })} ${localTimeZoneString}`;
    }

    // Handle weekly or hourly schedules
    const dayOfWeekNum = dayOfWeek === '?' ? null : parseInt(dayOfWeek, 10);

    // Create a date in UTC
    const utcDate = new Date();
    utcDate.setUTCHours(hour);
    utcDate.setUTCMinutes(minuteParts[0]);

    let nextRunTime: Date | undefined;

    const targetHourUTC = hour;
    const targetMinuteUTC = minuteParts[0];

    // --- Calculate Next Run Time based on Cron Pattern ---
    // The order handles more specific patterns (like specific year) before more general ones.

    // 1. Specific Date/Time (Year specified)
    if (year !== '*') {
      const specificYear = parseInt(year, 10);
      const specificMonth0Based = parseInt(month, 10) - 1;
      const specificDay = parseInt(dayOfMonth, 10);

      if (isNaN(specificYear) || isNaN(specificMonth0Based) || isNaN(specificDay))
        return 'Invalid schedule';

      const specificRunTime = new Date(
        Date.UTC(
          specificYear,
          specificMonth0Based,
          specificDay,
          targetHourUTC,
          targetMinuteUTC,
          0,
          0,
        ),
      );

      if (isNaN(specificRunTime.getTime())) return 'Invalid schedule';

      if (specificRunTime.getTime() < currentTime.getTime()) {
        // Already passed
        return `Not scheduled`;
      } else {
        // Future specific run
        return `Once on ${specificRunTime.toLocaleString(/* format options */)}`;
      }
    }
    // 2. Weekly Schedule (Day of Week specified, Year is '*' )
    else if (dayOfWeekNum !== null) {
      // --- Weekly ---
      // Finds the next date that matches the target day of week AND is after currentTime.
      // Needs to loop day by day, checking day of week and time.
      // Find the next occurrence of targetJSdayOfWeek at targetHourUTC:targetMinuteUTC
      const targetJSdayOfWeek = dayOfWeekNum === 1 ? 0 : dayOfWeekNum - 1; // 1(Sun)->0..7(Sat)->6
      let searchDate = new Date(currentTime);
      searchDate.setUTCSeconds(0, 0);

      // Loop forward day by day (max 7 checks needed + initial state = 8)
      // until we find the next future occurrence of the target day of week.
      for (let i = 0; i < 8; i++) {
        // Create a potential run time based on the current search day + target time
        let potentialRunTime = new Date(searchDate);
        potentialRunTime.setUTCHours(targetHourUTC);
        potentialRunTime.setUTCMinutes(targetMinuteUTC);

        // Check if this potential day matches the target day of week AND is in the future
        if (
          potentialRunTime.getUTCDay() === targetJSdayOfWeek &&
          potentialRunTime.getTime() > currentTime.getTime()
        ) {
          nextRunTime = potentialRunTime; // Found it
          break; // Exit loop
        }

        // If not found, advance the base search date to the START of the next day
        searchDate.setUTCDate(searchDate.getUTCDate() + 1);
        searchDate.setUTCHours(0, 0, 0, 0);

        if (i === 7) {
          console.error('Weekly loop safety break');
        } // Should not be hit
      }
    }
    // 3. Fixed Daily Schedule (Runs every day at a specific time)
    else if (
      (dayOfMonth === '1/1' || dayOfMonth === '*') &&
      cronParts[2] !== '*' &&
      !cronParts[2].includes('/') &&
      !cronParts[1].match(/[,-/]/)
    ) {
      // Calculates the target time for today. If it has passed, calculates it for tomorrow.
      let potentialRunTime = new Date(currentTime.getTime()); // Use copy
      potentialRunTime.setUTCHours(targetHourUTC);
      potentialRunTime.setUTCMinutes(targetMinuteUTC);
      potentialRunTime.setUTCSeconds(0, 0);

      if (potentialRunTime.getTime() <= currentTime.getTime()) {
        potentialRunTime.setUTCDate(potentialRunTime.getUTCDate() + 1);
        // Re-set time components after advancing day to be safe
        potentialRunTime.setUTCHours(targetHourUTC);
        potentialRunTime.setUTCMinutes(targetMinuteUTC);
      }
      nextRunTime = potentialRunTime;
    }
    // 4. Every N Days Schedule (DayOfMonth has a step like '1/2')
    else if (dayOfMonth.includes('/')) {
      // --- Every N Days ---
      // Finds the next calendar day matching the start/step pattern whose target time is after currentTime.
      // Requires looping day by day and checking the pattern `(day - start) % step === 0`.
      const [startDayStr, dayStepStr] = dayOfMonth.split('/');
      const startDay = parseInt(startDayStr, 10);
      const dayStep = parseInt(dayStepStr, 10);
      // ... validation ...

      let searchDate = new Date(currentTime.getTime()); // Use copy
      searchDate.setUTCHours(targetHourUTC);
      searchDate.setUTCMinutes(targetMinuteUTC);
      searchDate.setUTCSeconds(0, 0);

      // Loop forward day by day (max ~35 days) until we find the next future occurrence
      // matching the N-day step pattern.
      for (let i = 0; i < 35; i++) {
        const currentDayOfMonth = searchDate.getUTCDate();
        // Check if the current search day aligns with the start/step pattern
        const isDayMatch =
          currentDayOfMonth >= startDay && (currentDayOfMonth - startDay) % dayStep === 0;

        if (isDayMatch) {
          // If day matches pattern, create potential time for this day
          let potentialRunTime = new Date(searchDate);
          potentialRunTime.setUTCHours(targetHourUTC);
          potentialRunTime.setUTCMinutes(targetMinuteUTC);

          // Check if this potential time is in the future
          if (potentialRunTime.getTime() > currentTime.getTime()) {
            nextRunTime = potentialRunTime; // Found it!
            break; // Exit loop
          }
          // If time has passed for this matching day, loop continues to next day
        }

        // Advance searchDate to START of the next calendar day
        searchDate.setUTCDate(searchDate.getUTCDate() + 1);
        searchDate.setUTCHours(0, 0, 0, 0);
        if (i === 34) {
          console.error('N-Day loop safety break');
        }
      }
    }
    // 5. Monthly on Specific Day Schedule (DayOfMonth is specific number, Month is every month)
    else if (!isNaN(parseInt(dayOfMonth, 10)) && (month === '*' || month === '1/1')) {
      // --- Monthly on Specific Day ---
      // Finds the next month where the targetDayOfMonth exists and the target time is after currentTime.
      // Requires looping month by month, checking if day exists (e.g., 31st in Feb), and checking time.
      const targetDayOfMonth = parseInt(dayOfMonth, 10);
      // ... validation ...
      let searchDate = new Date(currentTime);
      searchDate.setUTCSeconds(0, 0);

      // Loop forward month by month (max 1 year) until we find the next future occurrence
      // on the specific target day of the month.
      for (let i = 0; i < 13; i++) {
        // Create the potential date for the target day in the current search month
        let potentialRunTime = new Date(searchDate);
        potentialRunTime.setUTCDate(targetDayOfMonth);
        potentialRunTime.setUTCHours(targetHourUTC);
        potentialRunTime.setUTCMinutes(targetMinuteUTC);

        // Verify the day actually exists in this month
        if (potentialRunTime.getUTCDate() === targetDayOfMonth) {
          // If day is valid, check if this time is in the future
          if (potentialRunTime.getTime() > currentTime.getTime()) {
            nextRunTime = potentialRunTime; // Found it
            break; // Exit loop
          }
          // If time has passed, loop continues to next month
        }
        // If day rolled over, loop continues to next month

        // Advance searchDate to the START of the next calendar month
        searchDate.setUTCDate(1);
        searchDate.setUTCMonth(searchDate.getUTCMonth() + 1);
        searchDate.setUTCHours(0, 0, 0, 0);

        if (i === 12) {
          console.error('Monthly loop safety break');
        }
      }
    }
    // 6. Every N Months on Specific Day Schedule (Month has step, DayOfMonth is specific number)
    else if (month.includes('/') && !isNaN(parseInt(dayOfMonth, 10))) {
      // --- Every N Months on Specific Day ---
      // Finds the next month matching the start/step pattern where the targetDayOfMonth exists
      // and the target time is after currentTime.
      // Similar to Monthly loop but with added month step check.
      const targetDayOfMonth = parseInt(dayOfMonth, 10);
      const [startMonthStr, monthStepStr] = month.split('/');
      const startMonth0Based = parseInt(startMonthStr, 10) - 1;
      const monthStep = parseInt(monthStepStr, 10);
      // ... validation ...

      let searchDate = new Date(currentTime);
      searchDate.setUTCSeconds(0, 0);

      // Loop forward month by month (max ~2 years) until we find the next future occurrence
      // matching the N-month step AND the specific day of month.
      for (let i = 0; i < 25; i++) {
        const currentSearchMonth0Based = searchDate.getUTCMonth();
        // Check if the month aligns with the startMonth/step pattern
        const isMonthMatch =
          currentSearchMonth0Based >= startMonth0Based &&
          (currentSearchMonth0Based - startMonth0Based) % monthStep === 0;

        if (isMonthMatch) {
          // If month matches, create the potential date for the target day in this month
          let potentialRunTime = new Date(searchDate);
          potentialRunTime.setUTCDate(targetDayOfMonth);
          potentialRunTime.setUTCHours(targetHourUTC);
          potentialRunTime.setUTCMinutes(targetMinuteUTC);

          // Verify the day actually exists in this month (e.g., avoids Feb 31st)
          if (potentialRunTime.getUTCDate() === targetDayOfMonth) {
            // If day is valid, check if this time is in the future
            if (potentialRunTime.getTime() > currentTime.getTime()) {
              nextRunTime = potentialRunTime; // Found it
              break; // Exit loop
            }
            // If time has passed for this valid day/month, loop will continue to next month
          }
          // If day rolled over (e.g., Feb 31 -> Mar 1), loop will continue to next month
        }

        // Advance searchDate to the START of the next calendar month
        searchDate.setUTCDate(1);
        searchDate.setUTCMonth(searchDate.getUTCMonth() + 1);
        searchDate.setUTCHours(0, 0, 0, 0);

        if (i === 24) {
          console.error('N-Month loop safety break');
        }
      }
    }
    // 7. Hourly Schedule (Fallback for other patterns, usually interval-based)
    else {
      // Calculates the next minute/hour based on the current time and minuteParts list.
      // Handles rolling over the hour (and potentially day/month/year via setUTCHours).
      // This is the most common case for simple intervals.
      const currentMinutes = currentTime.getUTCMinutes();
      const currentHours = currentTime.getUTCHours();
      let tempNextRunTime = new Date(currentTime);
      tempNextRunTime.setUTCSeconds(0, 0);

      // Need to handle multiple minutes from minuteParts here!
      let nextMinute = minuteParts.find((minute) => minute > currentMinutes);

      if (nextMinute !== undefined) {
        tempNextRunTime.setUTCMinutes(nextMinute);
        tempNextRunTime.setUTCHours(currentHours); // Keep current hour
      } else {
        // No more minutes in this hour, advance to next hour
        tempNextRunTime.setUTCMinutes(minuteParts[0]); // Use first minute in list
        tempNextRunTime.setUTCHours(currentHours + 1); // Advance hour (handles day/month/year roll)
      }
      nextRunTime = tempNextRunTime;
    }

    // --- Final Validation and Formatting ---
    if (!nextRunTime || isNaN(nextRunTime.getTime())) {
      console.error('Invalid date calculated or nextRunTime undefined:', {
        cronExpression,
        calculated: nextRunTime,
      });
      return cronToHumanReadable(cronExpression);
    }

    // Always use full date format in local time (using the mocked toLocaleString in tests)
    return `${nextRunTime.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })} ${localTimeZoneString}`;
  } catch (err) {
    // Handle unexpected errors during parsing or calculation
    console.error('Error parsing cron expression:', err);
    return cronToHumanReadable(cronExpression);
  }
};
